import React, { useEffect, useState } from 'react';
import { X, AlertTriangle, Loader2, CheckCircle, XCircle, LifeBuoy } from 'lucide-react';
import { getUserFacingBookingId } from '../utils/userFacingBookingId';

/**
 * Sprint A — Riya Cancel Booking Modal
 *
 * Two modes (driven by `mode` prop):
 *   • mode="cancel"       → POST /api/riya/cancel        (issued ticket; airline penalty applies)
 *   • mode="hold-cancel"  → POST /api/riya/hold-cancel   (release a held PNR; no charge)
 *
 * Steps:
 *   1. CONFIRM      → reason input + (cancel mode) PENALTY/CANCEL flag toggle
 *   2. SUBMITTING   → in-flight call to Riya
 *   3. DONE         → success summary + audit ticket reference
 *   4. ERROR        → Riya failed; offer to "Open Support Ticket"
 *   5. SUPPORT_DONE → support ticket opened
 *
 * Auth via httpOnly cookies — no token wiring needed.
 *
 * Props:
 *   open: bool
 *   onClose: () => void
 *   bookingId: string (our internal id)
 *   booking?: object   (used to pre-populate context like PNR)
 *   mode?: "cancel" | "hold-cancel"  (default "cancel")
 *   onCancelled?: () => void   // called after SUCCESS so caller can refresh list
 */
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = {
  CONFIRM: 'confirm',
  SUBMITTING: 'submitting',
  DONE: 'done',
  ERROR: 'error',
  SUPPORT_OPENING: 'support_opening',
  SUPPORT_DONE: 'support_done',
};

export default function RiyaCancelModal({
  open,
  onClose,
  bookingId,
  booking = {},
  mode = 'cancel',
  onCancelled,
}) {
  const isHold = mode === 'hold-cancel';
  const [step, setStep] = useState(STEPS.CONFIRM);
  const [flag, setFlag] = useState('PENALTY'); // PENALTY | CANCEL — only for issued cancel
  const [remarks, setRemarks] = useState('');
  const [errMsg, setErrMsg] = useState('');
  const [supportFallback, setSupportFallback] = useState(false);
  const [result, setResult] = useState(null); // success payload from /cancel
  const [supportTicketRef, setSupportTicketRef] = useState(null);

  // Reset on open
  useEffect(() => {
    if (!open) return;
    setStep(STEPS.CONFIRM);
    setFlag('PENALTY');
    setRemarks('');
    setErrMsg('');
    setSupportFallback(false);
    setResult(null);
    setSupportTicketRef(null);
  }, [open]);

  if (!open) return null;

  const submit = async () => {
    if (!isHold && (!remarks || remarks.trim().length < 3)) {
      setErrMsg('Please add a reason for cancellation (3+ characters).');
      return;
    }
    setStep(STEPS.SUBMITTING);
    setErrMsg('');
    try {
      const url = isHold ? `${API}/riya/hold-cancel` : `${API}/riya/cancel`;
      const body = isHold
        ? { booking_id: bookingId }
        : { booking_id: bookingId, flag, remarks: remarks.trim() };
      const resp = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await resp.json();
      if (!resp.ok) {
        const detail = data?.detail;
        const rawMsg = typeof detail === 'string' ? detail : (detail?.message || 'Riya cancel failed');
        const fb = typeof detail === 'object' && detail !== null ? !!detail.support_fallback : false;
        const supplierDown = resp.status === 502
          || /Proxy Error|Login|TravelAPI\.svc|ReadTimeout|Bad Gateway/i.test(String(rawMsg));
        setErrMsg(supplierDown
          ? 'Riya supplier is temporarily unreachable. Please retry in a few minutes.'
          : rawMsg);
        setSupportFallback(fb || supplierDown);
        setStep(STEPS.ERROR);
        return;
      }
      setResult(data);
      setStep(STEPS.DONE);
      if (onCancelled) onCancelled();
    } catch (e) {
      setErrMsg(e.message || 'Network error');
      setSupportFallback(true);
      setStep(STEPS.ERROR);
    }
  };

  const openSupportTicket = async () => {
    setStep(STEPS.SUPPORT_OPENING);
    try {
      const resp = await fetch(`${API}/riya/cancel-failure-ticket`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          booking_id: bookingId,
          action: isHold ? 'hold-cancel' : 'cancel',
          error_message: errMsg || 'Riya API error',
          user_remarks: remarks.trim(),
        }),
      });
      const data = await resp.json();
      if (!resp.ok) {
        const msg = typeof data?.detail === 'string' ? data.detail : (data?.detail?.message || 'Failed to open ticket');
        throw new Error(msg);
      }
      setSupportTicketRef(data.ticket_reference);
      setStep(STEPS.SUPPORT_DONE);
    } catch (e) {
      setErrMsg(`Could not open support ticket: ${e.message}`);
      setStep(STEPS.ERROR);
      setSupportFallback(false); // already tried; don't loop
    }
  };

  const pnr = booking.riya_pnr || booking.pnr || booking.airline_pnr || '—';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
      data-testid="riya-cancel-modal"
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
          <div className="flex items-center gap-2">
            <XCircle className="w-5 h-5 text-red-600" />
            <h3 className="text-lg font-bold text-gray-800">
              {isHold ? 'Release Hold PNR' : 'Cancel Booking'}
              <span className="ml-2 text-xs font-medium text-gray-500">(Riya)</span>
            </h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-700"
            aria-label="Close"
            data-testid="riya-cancel-modal-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {step === STEPS.CONFIRM && (
            <>
              <div className="flex gap-3 bg-amber-50 border border-amber-200 rounded-lg p-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                <div className="text-sm text-amber-900">
                  {isHold ? (
                    <>
                      <p className="font-semibold">This will release the held PNR.</p>
                      <p className="text-xs mt-1">Seats return to inventory. No airline charge applies.</p>
                    </>
                  ) : (
                    <>
                      <p className="font-semibold">This will cancel the issued ticket with the airline.</p>
                      <p className="text-xs mt-1">Cancellation charges apply per fare rules. Wallet refund (if any) is processed automatically.</p>
                    </>
                  )}
                </div>
              </div>

              <div className="text-sm text-gray-700 space-y-1">
                <p>Booking ID: <span className="font-mono font-semibold">{getUserFacingBookingId({ ...booking, booking_id: bookingId, id: bookingId })}</span></p>
                <p>Riya PNR: <span className="font-mono font-semibold">{pnr}</span></p>
              </div>

              {!isHold && (
                <>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 uppercase mb-2">Cancellation Type</label>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => setFlag('PENALTY')}
                        className={`px-3 py-2.5 rounded-lg text-sm border ${flag === 'PENALTY' ? 'bg-red-50 border-red-400 text-red-700 font-semibold' : 'border-gray-300 text-gray-600 hover:border-gray-400'}`}
                        data-testid="riya-cancel-flag-penalty"
                      >
                        With Penalty
                        <span className="block text-[10px] font-normal mt-0.5 opacity-80">Standard fare-rule charges</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setFlag('CANCEL')}
                        className={`px-3 py-2.5 rounded-lg text-sm border ${flag === 'CANCEL' ? 'bg-emerald-50 border-emerald-400 text-emerald-700 font-semibold' : 'border-gray-300 text-gray-600 hover:border-gray-400'}`}
                        data-testid="riya-cancel-flag-fullrefund"
                      >
                        Full Refund
                        <span className="block text-[10px] font-normal mt-0.5 opacity-80">Only if fare permits</span>
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 uppercase mb-2">Reason for cancellation *</label>
                    <textarea
                      value={remarks}
                      onChange={(e) => setRemarks(e.target.value)}
                      rows={3}
                      placeholder="e.g. Customer plan changed, refund to wallet"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                      data-testid="riya-cancel-remarks"
                    />
                  </div>
                </>
              )}

              {errMsg && <p className="text-xs text-red-600">{errMsg}</p>}

              <div className="flex gap-3 pt-2">
                <button
                  onClick={onClose}
                  className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium"
                  data-testid="riya-cancel-back-btn"
                >
                  Keep Booking
                </button>
                <button
                  onClick={submit}
                  className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium"
                  data-testid="riya-cancel-confirm-btn"
                >
                  {isHold ? 'Release Hold' : 'Confirm Cancellation'}
                </button>
              </div>
            </>
          )}

          {step === STEPS.SUBMITTING && (
            <div className="text-center py-8">
              <Loader2 className="w-10 h-10 text-red-600 animate-spin mx-auto mb-3" />
              <p className="text-sm font-semibold text-gray-800">
                {isHold ? 'Releasing hold with Riya…' : 'Cancelling with Riya…'}
              </p>
              <p className="text-xs text-gray-500 mt-1">Please don&apos;t close this window.</p>
            </div>
          )}

          {step === STEPS.DONE && (
            <div className="text-center py-4">
              <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
              <p className="text-base font-semibold text-gray-800">
                {isHold ? 'Hold PNR Released' : 'Booking Cancelled'}
              </p>
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mt-3 text-left text-sm space-y-1">
                <p><span className="text-gray-600">Riya PNR:</span> <span className="font-mono">{result?.riya_pnr || pnr}</span></p>
                {!isHold && (
                  <>
                    <p><span className="text-gray-600">Penalty:</span> <strong>₹{(result?.penalty_amount || 0).toLocaleString('en-IN')}</strong></p>
                    <p>
                      <span className="text-gray-600">Refund:</span>{' '}
                      <strong className="text-emerald-700">₹{(result?.refund_amount || 0).toLocaleString('en-IN')}</strong>
                      {result?.wallet_credited && <span className="ml-1 text-xs text-emerald-700">· wallet credited</span>}
                    </p>
                  </>
                )}
                {result?.audit_ticket_reference && (
                  <p>
                    <span className="text-gray-600">Audit reference:</span>{' '}
                    <span className="font-mono">{result.audit_ticket_reference}</span>
                  </p>
                )}
              </div>
              <button
                onClick={onClose}
                className="mt-4 w-full px-4 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-medium"
                data-testid="riya-cancel-done-btn"
              >
                Close
              </button>
            </div>
          )}

          {step === STEPS.ERROR && (
            <div className="py-2">
              <div className="text-center mb-3">
                <XCircle className="w-12 h-12 text-red-500 mx-auto mb-2" />
                <p className="text-base font-semibold text-gray-800">{isHold ? 'Hold release failed' : 'Cancellation failed'}</p>
                <p className="text-sm text-gray-600 mt-1 px-2">{errMsg}</p>
              </div>

              {supportFallback ? (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mt-2">
                  <div className="flex items-start gap-2">
                    <LifeBuoy className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-orange-900">
                      <p className="font-semibold">Need help finishing this cancellation?</p>
                      <p className="text-xs mt-1">
                        We can open an urgent support ticket for our ops team to complete this manually with Riya. They will refund your wallet (if applicable) and confirm with you.
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-3">
                    <button
                      onClick={onClose}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm"
                      data-testid="riya-cancel-error-close-btn"
                    >
                      Close
                    </button>
                    <button
                      onClick={openSupportTicket}
                      className="flex-1 px-3 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 text-sm font-medium"
                      data-testid="riya-cancel-open-support-btn"
                    >
                      Open Support Ticket
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={onClose}
                  className="mt-3 w-full px-4 py-2.5 bg-gray-700 text-white rounded-lg hover:bg-gray-800 font-medium"
                >
                  Close
                </button>
              )}
            </div>
          )}

          {step === STEPS.SUPPORT_OPENING && (
            <div className="text-center py-8">
              <Loader2 className="w-10 h-10 text-orange-600 animate-spin mx-auto mb-3" />
              <p className="text-sm font-semibold text-gray-800">Opening support ticket…</p>
            </div>
          )}

          {step === STEPS.SUPPORT_DONE && (
            <div className="text-center py-4">
              <LifeBuoy className="w-12 h-12 text-orange-500 mx-auto mb-3" />
              <p className="text-base font-semibold text-gray-800">Support ticket opened</p>
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mt-3 text-left text-sm">
                <p>
                  <span className="text-gray-600">Reference:</span>{' '}
                  <span className="font-mono font-semibold">{supportTicketRef}</span>
                </p>
                <p className="text-xs text-gray-600 mt-1">Our ops team will complete this cancellation with Riya manually and update you here.</p>
              </div>
              <button
                onClick={onClose}
                className="mt-4 w-full px-4 py-2.5 bg-orange-600 text-white rounded-lg hover:bg-orange-700 font-medium"
                data-testid="riya-cancel-support-done-btn"
              >
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
